import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiAddLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

import {
  getPurchaseOrders,
  deletePurchaseOrder,
} from "../services/purchaseService";

import "./UnitList.css"; // reuse same table UI

const PurchaseOrderListLayer = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // search
  const [search, setSearch] = useState("");

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await getPurchaseOrders();

      if (res?.status === "success") {
        setOrders(res.data || []);
      } else {
        toast.error(res?.message || "Failed to load purchase orders");
      }
    } catch {
      toast.error("API Error");
    }
    setLoading(false);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this purchase order?")) return;

    try {
      const res = await deletePurchaseOrder(id);
      if (res?.status === "success") {
        toast.success("Purchase order deleted");
        loadOrders();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("API Error");
    }
  };

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    return orders.filter((o) =>
      `${o.reference_no} ${o.invoice_no} ${o.status || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [orders, search]);

  /* ================= PAGINATION ================= */
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    pageSize === "all"
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* ================= HEADER ================= */}
      <div className="ul-header">
        <h4>Purchase Orders</h4>
        <Link to="/add-purchase-order" className="ul-add-btn">
          <RiAddLine /> Add Purchase Order
        </Link>
      </div>

      {/* ================= FILTERS ================= */}
      <div className="ul-filters">
        {/* SEARCH */}
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search purchase order..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* PAGE SIZE */}
        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              <th>PO No</th>
              <th>Invoice</th>
              <th>Date</th>
              <th>Grand Total</th>
              <th>Paid</th>
              <th>Due</th>
              <th>Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center text-muted">
                  No purchase orders found
                </td>
              </tr>
            ) : (
              paginatedData.map((o, index) => (
                <tr key={o.id}>
                  <td>
                    {(currentPage - 1) * effectivePageSize + index + 1}
                  </td>
                  <td>{o.reference_no}</td>
                  <td>{o.invoice_no}</td>
                  <td>{o.date}</td>
                  <td>{o.grand_total}</td>
                  <td>{o.paid}</td>
                  <td>{o.due}</td>
                  <td>
                    <span
                      className={`badge bg-${
                        o.status === "Partial" ? "warning" : "success"
                      }`}
                    >
                      {o.status || "Completed"}
                    </span>
                  </td>
                  <td className="ul-actions">
                    <Link
                      to={`/add-purchase-order/${o.id}`}
                      className="ul-btn edit"
                    >
                      <RiEdit2Line /> Edit
                    </Link>
                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(o.id)}
                    >
                      <RiDeleteBin6Line /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ================= PAGINATION ================= */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderListLayer;
